import React, { useState, useEffect } from "react";
import DashboardLayout from "../../../components/DashboardLayout";
import {
  getBannerAPI,
  updateBannerAPI,
  getPopupBannerAPI,
  updatePopupBannerAPI,
} from "../../../services/portal/admin/bannerService";
import {
  showErrorToastMsg,
  showSuccessToastMsg,
} from "../../../utility/ToastMessage";
import Loader from "../../../components/Loader";
import { convertFileToBase64, tableHeaderColor } from "../../../utility/Common";

const MAX_BANNER_IMAGE_BYTES = 1024 * 1024;

const bannerImageSrc = (value) => {
  if (!value || typeof value !== "string") return null;
  const v = value.trim();
  if (!v) return null;
  if (v.startsWith("http://") || v.startsWith("https://") || v.startsWith("data:")) {
    return v;
  }
  return `data:image/jpeg;base64,${v}`;
};

const BannerManagePage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    status: "1",
  });
  const [existingBannerImage, setExistingBannerImage] = useState(null);
  const [newBannerFile, setNewBannerFile] = useState(null);
  const [newBannerPreviewUrl, setNewBannerPreviewUrl] = useState(null);

  // Popup banner state
  const [popupLoading, setPopupLoading] = useState(true);
  const [popupSaving, setPopupSaving] = useState(false);
  const [popupFormData, setPopupFormData] = useState({
    title: "",
    description: "",
    buttonLink: "",
    status: "1",
  });
  const [existingPopupImage, setExistingPopupImage] = useState(null);
  const [newPopupFile, setNewPopupFile] = useState(null);
  const [newPopupPreviewUrl, setNewPopupPreviewUrl] = useState(null);

  useEffect(() => {
    return () => {
      if (newBannerPreviewUrl) URL.revokeObjectURL(newBannerPreviewUrl);
      if (newPopupPreviewUrl) URL.revokeObjectURL(newPopupPreviewUrl);
    };
  }, [newBannerPreviewUrl, newPopupPreviewUrl]);

  const loadBanner = async () => {
    setLoading(true);
    try {
      const res = await getBannerAPI(0);
      if (res && res.IsSuccessful && res.Data != null) {
        const raw = res.Data;
        const banner = Array.isArray(raw) ? raw[0] : raw;
        setFormData({
          title: banner.Title ?? banner.title ?? "",
          description: banner.Description ?? banner.description ?? "",
          status: String(banner.Status ?? banner.status ?? 1),
        });
        setExistingBannerImage(banner.Image ?? null);
      } else {
        showErrorToastMsg(res?.Message || "Failed to load banner");
      }
    } catch (error) {
      console.error("Error loading banner:", error);
      showErrorToastMsg("Failed to load banner");
    } finally {
      setLoading(false);
    }
  };

  const loadPopupBanner = async () => {
    setPopupLoading(true);
    try {
      const res = await getPopupBannerAPI(0);
      if (res && res.IsSuccessful && res.Data != null) {
        const raw = res.Data;
        const banner = Array.isArray(raw) ? raw[0] : raw;
        setPopupFormData({
          title: banner.Title ?? banner.title ?? "",
          description: banner.Description ?? banner.description ?? "",
          buttonLink: banner.URL ?? "",
          status: String(banner.Status ?? banner.status ?? 1),
        });
        setExistingPopupImage(banner.Image ?? null);
      } else {
        showErrorToastMsg(res?.Message || "Failed to load popup banner");
      }
    } catch (error) {
      console.error("Error loading popup banner:", error);
      showErrorToastMsg("Failed to load popup banner");
    } finally {
      setPopupLoading(false);
    }
  };

  useEffect(() => {
    loadBanner();
    loadPopupBanner();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePopupChange = (e) => {
    const { name, value } = e.target;
    setPopupFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePopupFileChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      showErrorToastMsg("Please select an image file");
      return;
    }
    if (file.size > MAX_BANNER_IMAGE_BYTES) {
      showErrorToastMsg("Popup banner image must be smaller than 1 MB");
      return;
    }
    setNewPopupPreviewUrl((prevUrl) => {
      if (prevUrl) URL.revokeObjectURL(prevUrl);
      return URL.createObjectURL(file);
    });
    setNewPopupFile(file);
  };

  const clearNewPopupImage = () => {
    setNewPopupPreviewUrl((prevUrl) => {
      if (prevUrl) URL.revokeObjectURL(prevUrl);
      return null;
    });
    setNewPopupFile(null);
  };

  const handlePopupSubmit = async (e) => {
    e.preventDefault();
    setPopupSaving(true);
    try {
      const params = {
        id: 2,
        title: popupFormData.title.trim(),
        description: popupFormData.description.trim(),
        url: popupFormData.buttonLink.trim(),
        status: Number(popupFormData.status),
      };
      if (newPopupFile) {
        params.image = await convertFileToBase64(newPopupFile);
      } else {
        params.image = existingPopupImage;
      }
      const res = await updatePopupBannerAPI(params);
      if (res && res.IsSuccessful) {
        showSuccessToastMsg(res.Message || "Popup banner updated successfully");
        clearNewPopupImage();
        await loadPopupBanner();
      } else {
        showErrorToastMsg(res?.Message || "Something went wrong, please try again later");
      }
    } catch (error) {
      console.error("Error updating popup banner:", error);
      showErrorToastMsg("Failed to update popup banner");
    } finally {
      setPopupSaving(false);
    }
  };

  const handleBannerFileChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      showErrorToastMsg("Please select an image file");
      return;
    }
    if (file.size > MAX_BANNER_IMAGE_BYTES) {
      showErrorToastMsg("Banner image must be smaller than 1 MB");
      return;
    }
    setNewBannerPreviewUrl((prevUrl) => {
      if (prevUrl) URL.revokeObjectURL(prevUrl);
      return URL.createObjectURL(file);
    });
    setNewBannerFile(file);
  };

  const clearNewImage = () => {
    setNewBannerPreviewUrl((prevUrl) => {
      if (prevUrl) URL.revokeObjectURL(prevUrl);
      return null;
    });
    setNewBannerFile(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setSaving(true);
    try {
      const params = {
        id: 1,
        title: formData.title.trim(),
        description: formData.description.trim(),
        status: Number(formData.status),
      };
      if (newBannerFile) {
        params.image = await convertFileToBase64(newBannerFile);
      } else {
        params.image = existingBannerImage;
      }
      const res = await updateBannerAPI(params);
      if (res && res.IsSuccessful) {
        showSuccessToastMsg(res.Message || "Banner updated successfully");
        clearNewImage();
        await loadBanner();
      } else {
        showErrorToastMsg(
          res?.Message || "Something went wrong, please try again later",
        );
      }
    } catch (error) {
      console.error("Error updating banner:", error);
      showErrorToastMsg("Failed to update banner");
    } finally {
      setSaving(false);
    }
  };

  const displaySrc = newBannerPreviewUrl || bannerImageSrc(existingBannerImage);
  const popupDisplaySrc = newPopupPreviewUrl || bannerImageSrc(existingPopupImage);

  if (loading && popupLoading) {
    return (
      <DashboardLayout title="Banner" subtitle="Manage home banner">
        <Loader />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Banner" subtitle="Manage home banner">
      <div className="space-y-8">
        <h3 className="text-lg font-medium text-gray-900">Manage Banner</h3>

        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden max-w-3xl">
          <div
            className={`${tableHeaderColor} px-6 py-4 border-b border-gray-200`}
          >
            <h4 className="text-md font-medium text-white">Update banner</h4>
          </div>
          <div className="p-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="Banner title"
                  className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div> */}

              {/* <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={4}
                  placeholder="Banner description"
                  className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div> */}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Banner image
                </label>
                <input
                  type="file"
                  name="bannerImage"
                  accept="image/*"
                  onChange={handleBannerFileChange}
                  className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Images only, maximum size 1 MB. Leave empty to keep the current
                  image.
                </p>
                {newBannerFile && (
                  <div className="mt-2 flex items-center gap-2">
                    <span className="text-xs text-gray-600">
                      Selected: {newBannerFile.name}
                    </span>
                    <button
                      type="button"
                      onClick={clearNewImage}
                      className="text-xs text-red-600 hover:text-red-800 font-medium"
                    >
                      Remove
                    </button>
                  </div>
                )}
              </div>

              {displaySrc && (
                <div>
                  <p className="block text-sm font-medium text-gray-700 mb-2">
                    Preview
                  </p>
                  <div className="border border-gray-200 rounded-lg p-2 bg-gray-50 inline-block max-w-full">
                    <img
                      src={displaySrc}
                      alt="Banner preview"
                      className="max-w-full h-auto rounded-md max-h-64 object-contain"
                    />
                  </div>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status <span className="text-red-500">*</span>
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                  className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  required
                >
                  <option value="1">Active</option>
                  <option value="3">Inactive</option>
                </select>
              </div>

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={saving}
                  className={`text-md font-medium px-4 py-2 rounded-md transition-colors ${
                    saving
                      ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                      : "bg-primary-600 text-white hover:bg-primary-700"
                  }`}
                >
                  {saving ? "Saving..." : "Update banner"}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Popup Banner Section */}
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden max-w-3xl">
          <div className={`${tableHeaderColor} px-6 py-4 border-b border-gray-200`}>
            <h4 className="text-md font-medium text-white">Update popup banner</h4>
          </div>
          <div className="p-6">
            {popupLoading ? (
              <Loader />
            ) : (
              <form onSubmit={handlePopupSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={popupFormData.title}
                    onChange={handlePopupChange}
                    placeholder="Popup banner title"
                    className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={popupFormData.description}
                    onChange={handlePopupChange}
                    rows={3}
                    placeholder="Popup banner description"
                    className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Button Link
                  </label>
                  <input
                    type="text"
                    name="buttonLink"
                    value={popupFormData.buttonLink}
                    onChange={handlePopupChange}
                    placeholder="https://example.com"
                    className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Popup banner image
                  </label>
                  <input
                    type="file"
                    name="popupBannerImage"
                    accept="image/*"
                    onChange={handlePopupFileChange}
                    className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Images only, maximum size 1 MB. Leave empty to keep the current image.
                  </p>
                  {newPopupFile && (
                    <div className="mt-2 flex items-center gap-2">
                      <span className="text-xs text-gray-600">
                        Selected: {newPopupFile.name}
                      </span>
                      <button
                        type="button"
                        onClick={clearNewPopupImage}
                        className="text-xs text-red-600 hover:text-red-800 font-medium"
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>

                {popupDisplaySrc && (
                  <div>
                    <p className="block text-sm font-medium text-gray-700 mb-2">
                      Preview
                    </p>
                    <div className="border border-gray-200 rounded-lg p-2 bg-gray-50 inline-block max-w-full">
                      <img
                        src={popupDisplaySrc}
                        alt="Popup banner preview"
                        className="max-w-full h-auto rounded-md max-h-64 object-contain"
                      />
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="status"
                    value={popupFormData.status}
                    onChange={handlePopupChange}
                    className="text-sm font-medium w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    required
                  >
                    <option value="1">Active</option>
                    <option value="3">Inactive</option>
                  </select>
                </div>

                <div className="pt-2">
                  <button
                    type="submit"
                    disabled={popupSaving}
                    className={`text-md font-medium px-4 py-2 rounded-md transition-colors ${
                      popupSaving
                        ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                        : "bg-primary-600 text-white hover:bg-primary-700"
                    }`}
                  >
                    {popupSaving ? "Saving..." : "Update popup banner"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default BannerManagePage;
